import { Grid } from '@material-ui/core';
import { Select, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import { DeficiencyListModified } from '../../models/models';
import type { ConditionRating } from '../../rxdb/conditionRating/conditionRating.types';
import type { OperationalDowntime } from '../../rxdb/operationalDowntime/operationalDowntime.types';
import type { TrafficSafetyScore } from '../../rxdb/trafficSafetyScore/trafficSafetyScore.types';
import type { Urgency } from '../../rxdb/urgency/urgency.types';
import { ComponentInfo } from '../../utilities/enums';
import { isNull } from '../../utilities/utilityFunctions';
import useStyles from './RiskAssessment.styles';

interface Props {
  conditionRatingListData: ConditionRating[];
  urgencyListData: Urgency[];
  operationalDowntimeListData: OperationalDowntime[];
  trafficSafetyScoreListData: TrafficSafetyScore[];
  deficiencyPayload: DeficiencyListModified;
  validateInputs: () => void;
  trafficSafetyId: number;
  setValid: React.Dispatch<React.SetStateAction<boolean>>;
  componentAreaLength: number;
  setIsChange: (val: boolean) => void;
  setDeficiencyPayload: React.Dispatch<React.SetStateAction<DeficiencyListModified>>;
  isEditable: boolean;
}

const RiskAssessmentPanel: React.FC<Props> = ({
  deficiencyPayload,
  conditionRatingListData,
  urgencyListData,
  operationalDowntimeListData,
  trafficSafetyScoreListData,
  validateInputs,
  trafficSafetyId,
  setValid,
  componentAreaLength,
  setIsChange,
  setDeficiencyPayload,
  isEditable,
}) => {
  const classes = useStyles();
  const [trafficSafetyData, setTrafficSafetyData] = useState(
    isNull(deficiencyPayload, ComponentInfo.TRAFFIC_SAFETY_SCORE, ''),
  );

  const handleChangeRisk = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChange(true);
    const { name, value } = event.target;
    setDeficiencyPayload(prev => {
      const clone = { ...prev };
      clone.isChanged = true;
      switch (name) {
        case ComponentInfo.OPERTION_DOWNTIME:
          clone.inspectorOperationalDowntime = Number(value);
          break;
        case ComponentInfo.CONDITION_RATING:
          clone.inspectorConditionRating = Number(value);
          break;
        case ComponentInfo.URGENCY:
          clone.inspectorUrgency = Number(value);
          break;
        default:
          break;
      }
      return clone;
    });
    validateInputs();
  };

  useEffect(() => {
    validateInputs();
  }, [deficiencyPayload]);

  const updateTrafficSafetyScore = async (TrafficScoreId: number) => {
    const clone = { ...deficiencyPayload };
    if (TrafficScoreId) {
      clone.inspectorTrafficScore = TrafficScoreId;
      setTrafficSafetyData(TrafficScoreId);
    } else {
      clone.inspectorTrafficScore = null;
      setTrafficSafetyData('');
      setValid(true);
    }
    setDeficiencyPayload(clone);
  };

  useEffect(() => {
    updateTrafficSafetyScore(trafficSafetyId);
  }, [trafficSafetyId, trafficSafetyScoreListData]);

  return (
    <Grid className={classes.outerGrid} xs={12} item container>
      <Grid container spacing={2}>
        <Grid item container xs={12} alignItems="center">
          <Grid item xs={12} className={classes.riskAssesmentGrid}>
            <Typography variant="h6">Risk Assesment</Typography>
            <Typography variant="caption">
              <span>
                Fields with<span className={classes.requierd}> *</span>are mandatory
              </span>
            </Typography>
            <hr className={`${classes.breakLine} ${classes.checkboxLabel}`} />
          </Grid>
          <Grid item xs={12} className={classes.conditionRating}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Condition Rating
                  <span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.CONDITION_RATING}
                list={conditionRatingListData}
                required
                getOptionLabel={list => `${list?.sort} - ${list?.name}`}
                getOptionValue={list => list?.sort}
                onChange={handleChangeRisk}
                placeholder="select Condition Rating"
                defaultValue={isNull(deficiencyPayload, ComponentInfo.CONDITION_RATING, '')}
                disabled={!isEditable}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                noMinHeight
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Urgency<span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.URGENCY}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                list={urgencyListData}
                required
                getOptionLabel={list => `${list?.sort} - ${list?.name}`}
                getOptionValue={list => list?.sort}
                onChange={handleChangeRisk}
                defaultValue={isNull(deficiencyPayload, ComponentInfo.URGENCY, '')}
                disabled={!isEditable}
                placeholder="select urgency"
                data-cy={ComponentInfo.URGENCY}
                noMinHeight
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.operationalDownTimeGrid}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Operational Downtime
                  <span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.OPERTION_DOWNTIME}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                list={operationalDowntimeListData}
                required
                getOptionLabel={list => `${list?.sort} - ${list?.name}`}
                getOptionValue={list => list?.sort}
                onChange={handleChangeRisk}
                disabled={!isEditable}
                placeholder="Select Operational Downtime"
                defaultValue={isNull(deficiencyPayload, ComponentInfo.OPERTION_DOWNTIME, '')}
                noMinHeight
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className={`fieldLabel ${classes.trafficSafety}`}>
                <span>
                  Traffic Safety Score<span> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.TRAFFIC_SAFETY_SCORE}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                disabled
                list={trafficSafetyScoreListData}
                getOptionLabel={list => `${list?.sort} - ${list?.name}`}
                getOptionValue={list => list?.sort}
                placeholder="Select Traffic Safty Score"
                value={
                  trafficSafetyData
                    ? trafficSafetyData
                    : isNull(deficiencyPayload, ComponentInfo.TRAFFIC_SAFETY_SCORE, '')
                }
                noMinHeight
              />
              {trafficSafetyData === '' && componentAreaLength ? (
                <Typography variant="subtitle2" className="fieldLabel" color="error">
                  Selected component location does not have traffic safety score
                </Typography>
              ) : (
                ''
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RiskAssessmentPanel;
